import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { from } from 'rxjs';
import { environment } from '../../environments/environment';

import { PromoPlanHeader } from './promo-plan.model';
import { PromoPlanDetail } from './promo-plan-detail.model';
import { Page } from '../models/page-wrapper.model';
import { PromoPlanAttachment } from './promo-plan-attachment.model';
import { PickerRecentCacheService } from '../shared/services/picker-recent-cache.service';
import { PickerRecentRecord } from '../shared/components/entity-dropdown-picker/picker-recent.model';


export interface PromoPlanSearchParams {
  promoPlanIds?: number[];
  customerNumbers?: string[];
  customerNames?: string[];
  promoTypes?: string[];
  cities?: string[];
  channels?: string[];
  batchNumbers?: string[];
  statuses?: string[];
  brands?: string[];
  subBrands?: string[];
  discountPercentage?: string;
  startDateFrom?: string; // ISO string format: 'YYYY-MM-DD'
  startDateTo?: string;   // ISO string format: 'YYYY-MM-DD'
  endDateFrom?: string;   // ISO string format: 'YYYY-MM-DD'
  endDateTo?: string;     // ISO string format: 'YYYY-MM-DD'
  timelineStages?: string[];
  // Pagination and sorting parameters
  page?: number;
  size?: number;
  sort?: string; // e.g., 'promoDescription,asc'
}

@Injectable({
  providedIn: 'root'
})
export class PromoPlanService {

  private apiUrl = `${environment.restApiUrl}promo-plans`;
  private readonly pickerEntityKey = 'promo-plan';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  // Method to upload the promo plan Excel file
  uploadPromoPlan(sessionId: number, fileUrl: string): Observable<string> { // Replace 'any' with your expected backend response model
    const url = `${this.apiUrl}/sessions/${sessionId}/promo-plan`;
    return this.http.post<string>(url, { fileUrl });
  }

  private buildSearchParams(searchParams: PromoPlanSearchParams): HttpParams {
    let params = new HttpParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (value instanceof Date) {
          params = params.append(key, value.toISOString().split('T')[0]); // Format as YYYY-MM-DD
        } else if (Array.isArray(value)) {
          value.forEach(item => params = params.append(key, item));
        } else {
          params = params.append(key, String(value));
        }
      }
    });
    return params;
  }

  // --- PromoPlanHeader Endpoints ---

  getAllPromoPlanHeaders(): Observable<PromoPlanHeader[]> {
    return this.http.get<PromoPlanHeader[]>(`${this.apiUrl}/headers`);
  }

  searchPromoPlanHeaders(searchParams: PromoPlanSearchParams): Observable<Page<PromoPlanHeader>> {
    const params = this.buildSearchParams(searchParams);
    return this.http.get<Page<PromoPlanHeader>>(`${this.apiUrl}/headers/search`, { params });
  }

  getPromoPlanHeaderById(id: number): Observable<PromoPlanHeader> {
    return this.http.get<PromoPlanHeader>(`${this.apiUrl}/headers/${id}`);
  }

  loadRecentPromoPlans(limit = 10): Observable<PromoPlanHeader[]> {
    return from(this.recentPickerCache.loadRecent<PromoPlanHeader>(this.pickerEntityKey, limit));
  }

  getFreshPromoPlanById(id: number | string): Observable<PromoPlanHeader> {
    return this.getPromoPlanHeaderById(Number(id));
  }

  rememberPromoPlan(promoPlan: PromoPlanHeader): Observable<void> {
    const record: PickerRecentRecord<PromoPlanHeader> = {
      key: `${this.pickerEntityKey}::${promoPlan.promoPlanId}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: promoPlan.promoPlanId!,
      label: `Promo Plan# ${promoPlan.promoPlanId} - ${promoPlan.promoDescription}`,
      payload: promoPlan,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createPromoPlanHeader(header: PromoPlanHeader): Observable<PromoPlanHeader> {
    return this.http.post<PromoPlanHeader>(`${this.apiUrl}/headers`, header);
  }

  updatePromoPlanHeader(id: number, header: PromoPlanHeader): Observable<PromoPlanHeader> {
    return this.http.put<PromoPlanHeader>(`${this.apiUrl}/headers/${id}`, header);
  }

  deletePromoPlanHeader(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/headers/${id}`);
  }

  uploadAttachments(promoPlanId: number, attachments: PromoPlanAttachment[]): Observable<PromoPlanAttachment[]> {
    const url = `${this.apiUrl}/headers/${promoPlanId}/attachments`;
    return this.http.post<PromoPlanAttachment[]>(url, attachments);
  }

  getAttachmentViewUrl(promoPlanId: number, attachmentId: number): Observable<{ url: string }> {
    const url = `${this.apiUrl}/headers/${promoPlanId}/attachments/${attachmentId}/view-url`;
    return this.http.get<{ url: string }>(url);
  }

  // --- PromoPlanDetail Endpoints ---

  searchPromoPlanDetails(searchParams: PromoPlanSearchParams): Observable<Page<PromoPlanDetail>> {
    const params = this.buildSearchParams(searchParams);
    return this.http.get<Page<PromoPlanDetail>>(`${this.apiUrl}/details/search`, { params });
  }

  getAllPromoPlanDetails(): Observable<PromoPlanDetail[]> {
    return this.http.get<PromoPlanDetail[]>(`${this.apiUrl}/details`);
  }

  getPromoPlanDetailById(id: number): Observable<PromoPlanDetail> {
    return this.http.get<PromoPlanDetail>(`${this.apiUrl}/details/${id}`);
  }

  getPromoPlanDetailsByHeaderId(headerId: number): Observable<PromoPlanDetail[]> {
    return this.http.get<PromoPlanDetail[]>(`${this.apiUrl}/details/header/${headerId}`);
  }

  createPromoPlanDetail(detail: PromoPlanDetail, headerId: number): Observable<PromoPlanDetail> {
    return this.http.post<PromoPlanDetail>(`${this.apiUrl}/details/header/${headerId}`, detail);
  }

  updatePromoPlanDetail(id: number, detail: PromoPlanDetail, headerId: number): Observable<PromoPlanDetail> {
    return this.http.put<PromoPlanDetail>(`${this.apiUrl}/details/${id}/header/${headerId}`, detail);
  }

  deletePromoPlanDetail(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/details/${id}`);
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

}
